import React, { useState, useEffect } from "react";
import { useDarkMode } from '../contexts/DarkModeContext';
import { Mic, MicOff } from 'lucide-react';

function MicButton({
  isTyping,
  speechRecognitionRef,
  setInterimTranscript,
  setInputText,
  setIsMicActive,
  isMicActive,
  setVoiceText,
  setHasVoiceInput,
  setVoiceStartIndex,
  value,
}) {
  const [recognitionAvailable, setRecognitionAvailable] = useState(false);
  const { isDarkMode } = useDarkMode();
  const voiceTextStartPosRef = React.useRef(null);

  // Test if webkitSpeechRecognition is available in the browser or not
  useEffect(() => {
    if (!("webkitSpeechRecognition" in window)) {
      console.warn("Web Speech API not supported in this browser.");
    } else {
      setRecognitionAvailable(true);
    }
  }, [recognitionAvailable]);
  //Cleanup SpeechRecognitionRef instance
  useEffect(() => {
    return () => {
      if (speechRecognitionRef.current) {
        speechRecognitionRef.current.stop();
      }
    };
  }, [speechRecognitionRef]);

  const handleMicClick = () => {
    // Always create a fresh recognition instance
    if (speechRecognitionRef.current) {
      // Clean up any existing instance
      try {
        speechRecognitionRef.current.stop();
      } catch (e) {
        console.error('Previous recognition cleanup');
      }
      speechRecognitionRef.current = null;
    }

    const recognition = new window.webkitSpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    
    // Increase max alternatives for better accuracy (optional)
    recognition.maxAlternatives = 1;
    
    // Store final transcript accumulator on the recognition object itself
    recognition.finalTranscript = "";

    recognition.onstart = () => {
      // Save the current input position when starting voice input
      const currentLength = value.length;
      voiceTextStartPosRef.current = currentLength;
      setVoiceStartIndex(currentLength);
      
      // IMMEDIATELY set hasVoiceInput to show tick/cross icons
      setHasVoiceInput(true);
      setIsMicActive(true);
    };

    recognition.onresult = (event) => {
      let interimTranscript = "";
      
      // Process all results in the current event
      // The event contains all results from this recognition session
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          // Accumulate final transcripts across all events
          recognition.finalTranscript += transcript + " ";
        } else {
          interimTranscript += transcript;
        }
      }
      
      // Show interim results in real-time
      setInterimTranscript(interimTranscript);
      
      // Build the complete voice text (all final transcripts + current interim)
      const completeVoiceText = recognition.finalTranscript + interimTranscript;
      
      // Update input with voice text in real-time
      setInputText((prevText) => {
        const textBeforeVoice = prevText.substring(0, voiceTextStartPosRef.current);
        const newFullText = textBeforeVoice + completeVoiceText;
        setVoiceText(completeVoiceText);
        return newFullText;
      });
    };

    recognition.onerror = (event) => {
      console.error("Speech Recognition Error: ", event.error);
      
      // Handle different error types appropriately
      if (event.error === 'no-speech') {
        // Don't stop - just continue listening
        return;
      }
      
      if (event.error === 'aborted') {
        // This is expected when user clicks tick/cross
        return;
      }
      
      if (event.error === 'audio-capture') {
        console.error('Audio capture error - check microphone permissions');
        setIsMicActive(false);
        setHasVoiceInput(false);
        alert('Microphone access error. Please check your microphone permissions.');
        return;
      }
      
      if (event.error === 'network') {
        console.error('Network error - speech recognition requires internet');
        // Try to continue - the onend handler will restart
        return;
      }
      
      // For other critical errors, stop recognition
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        console.error('Critical error, stopping recognition');
        setIsMicActive(false);
        setHasVoiceInput(false);
      }
    };

    recognition.onend = () => {
      
      // Auto-restart if still actively recording (not if user clicked tick/cross)
      // This ensures continuous recording without interruption
      if (isMicActive && speechRecognitionRef.current === recognition) {
        try {
          // Small delay to prevent rapid restart issues
          setTimeout(() => {
            if (speechRecognitionRef.current === recognition && isMicActive) {
              recognition.start();
            }
          }, 100);
        } catch (e) {
          console.error('Recognition restart failed:', e);
          // If restart fails, try creating a new instance
          if (isMicActive) {
            try {
              const previousTranscript = recognition.finalTranscript || "";
              
              const newRecognition = new window.webkitSpeechRecognition();
              newRecognition.continuous = true;
              newRecognition.interimResults = true;
              newRecognition.lang = 'en-US';
              newRecognition.maxAlternatives = 1;
              
              // Preserve the accumulated transcript
              newRecognition.finalTranscript = previousTranscript;
              
              // Copy over the event handlers
              newRecognition.onstart = recognition.onstart;
              newRecognition.onresult = recognition.onresult;
              newRecognition.onerror = recognition.onerror;
              newRecognition.onend = recognition.onend;
              
              speechRecognitionRef.current = newRecognition;
              newRecognition.start();
            } catch (err) {
              console.error('Failed to create new recognition instance:', err);
            }
          }
        }
      } else {
        console.warn('Not restarting - recording was stopped by user');
      }
    };

    speechRecognitionRef.current = recognition;
    
    // Start recording
    try {
      recognition.start();
    } catch (e) {
      console.error('Failed to start recognition:', e);
    }
  };

  return (
    <>
      {recognitionAvailable ? (
        <button
          onClick={handleMicClick}
          disabled={isTyping || isMicActive}
          aria-label={isMicActive ? "Recording..." : "Start voice input"}
          className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
            isMicActive
              ? 'bg-red-500 text-white animate-pulse cursor-not-allowed'
              : isDarkMode
              ? 'bg-slate-800/60 text-slate-300 hover:bg-slate-800/80'
              : 'bg-slate-50/90 text-slate-600 hover:bg-slate-100'
          } ${isTyping ? 'opacity-50 cursor-not-allowed' : ''}`}
          style={{
            border: isMicActive 
              ? '1px solid rgb(220, 38, 38)' 
              : isDarkMode 
              ? '1px solid rgba(51, 65, 85, 0.6)' 
              : '1px solid rgba(226, 232, 240, 0.8)',
            boxShadow: isMicActive 
              ? 'none' 
              : isDarkMode 
              ? '0 2px 8px rgba(0, 0, 0, 0.2)' 
              : '0 2px 8px rgba(0, 0, 0, 0.05)'
          }}
        >
          <Mic className="w-4 h-4" fill={isMicActive ? "currentColor" : "none"} />
        </button>
      ) : (
        <div title="Mic is not supported in this browser">
          <button 
            disabled
            className={`w-9 h-9 rounded-full flex items-center justify-center opacity-50 cursor-not-allowed ${
              isDarkMode
                ? 'bg-slate-800/40 text-slate-600'
                : 'bg-slate-200/60 text-slate-400'
            }`}
            style={{
              border: isDarkMode ? '1px solid rgba(51, 65, 85, 0.4)' : '1px solid rgba(226, 232, 240, 0.6)'
            }}
          >
            <MicOff className="w-4 h-4" />
          </button>
        </div>
      )}
    </>
  );
}

export default MicButton;
